/**
 * Mobile Box Score Tile Component
 * Horizontal scrolling tile with tab-based navigation and new table format
 * Decoupled from desktop version
 */

import React, { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { dynastyTheme } from '../../../services/colorService';
import { formatBattingAvg, formatERA } from '../../../services/statFormatUtils';
import { MobilePlayerModal } from './player';
import { MobileDynastyTable } from './MobileDynastyTable';
// Bible § 14: box-score columns derived from league_settings.scoring_categories,
// not a hardcoded list. Shared single source of truth with the full box-score
// page (MobileTeamBoxScore) + MobileTeamHome via buildMobileBoxScoreColumns.
import { buildMobileBoxScoreColumns } from '../utils/boxScoreColumns';

// Tile CONTAINER chrome (League Home v2): flush panel — dark #0d0d14 fill, NO
// border-radius, NO side borders, just a top + bottom hairline at the standard
// border token (withAlpha(slate,0.5) = rgba(55,65,81,0.5)), same weight as the
// standings section separators. Replaces the old rounded `card.base` card. The
// BoxMini table inside (MobileDynastyTable) is untouched.
const TILE_CHROME = {
  background: dynastyTheme.tokens.colors.ground.screen,
  borderRadius: 0,
  borderTop: `1px solid ${dynastyTheme.tokens.colors.border}`,
  borderBottom: `1px solid ${dynastyTheme.tokens.colors.border}`,
  borderLeft: 0,
  borderRight: 0,
};

const MobileBoxScoreTile = ({
  teamId,
  teamName,
  teamLogoUrl,
  boxScoreData,
  loading,
  error,
  date,
  onViewFull,
  leagueId,
  scoringCategories
}) => {
  const [activeTab, setActiveTab] = useState('hitting');
  const [isPlayerModalOpen, setIsPlayerModalOpen] = useState(false);
  const [selectedPlayerId, setSelectedPlayerId] = useState(null);

  // Loading skeleton
  if (loading) {
    return (
      <div className="flex-shrink-0 w-80">
        <div className="h-96 animate-pulse" style={TILE_CHROME}>
          <div className="p-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-neutral-700"></div>
              <div className="flex-1">
                <div className="h-4 bg-neutral-700 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-neutral-700 rounded w-1/2"></div>
              </div>
            </div>
            <div className="h-32 bg-neutral-700 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex-shrink-0 w-80">
        <div style={TILE_CHROME}>
          <div className="p-4">
            <div className="flex items-center gap-3 mb-3">
              {teamLogoUrl ? (
                <img 
                  src={teamLogoUrl} 
                  alt={teamName}
                  className="w-12 h-12 rounded object-cover border-2 border-neutral-700"
                />
              ) : (
                <div className="w-12 h-12 rounded bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center text-black font-bold text-sm">
                  {teamName.substring(0, 2).toUpperCase()}
                </div>
              )}
              <div className="flex-1">
                <h4 className={`font-semibold ${dynastyTheme.classes.text.white}`}>{teamName}</h4>
                <p className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>{date}</p>
              </div>
            </div>
            <div className={`text-center py-12 ${dynastyTheme.classes.text.neutralLight}`}>
              <p className="text-sm">Unable to load box score</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No data / No games played
  if (!boxScoreData || !boxScoreData.box_score) {
    return (
      <div className="flex-shrink-0 w-80">
        <div style={TILE_CHROME}>
          <div className="p-4">
            <div className="flex items-center gap-3 mb-3">
              {teamLogoUrl ? (
                <img 
                  src={teamLogoUrl} 
                  alt={teamName}
                  className="w-12 h-12 rounded object-cover border-2 border-neutral-700"
                />
              ) : (
                <div className="w-12 h-12 rounded bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center text-black font-bold text-sm">
                  {teamName.substring(0, 2).toUpperCase()}
                </div>
              )}
              <div className="flex-1">
                <h4 className={`font-semibold ${dynastyTheme.classes.text.white}`}>{teamName}</h4>
                <p className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>{date}</p>
              </div>
            </div>
            <div className={`text-center py-12 ${dynastyTheme.classes.text.neutralLight}`}>
              <p className="text-sm">No games yesterday</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { box_score } = boxScoreData;
  const { hitting = [], pitching = [], team_totals = {} } = box_score;

  // Navigate to full box score
  const handleViewFull = () => {
    if (onViewFull) {
      onViewFull(teamId);
    }
  };

  const handlePlayerClick = (playerId) => {
    setSelectedPlayerId(playerId?.toString());
    setIsPlayerModalOpen(true);
  };

  const handleClosePlayer = () => {
    setIsPlayerModalOpen(false);
    setSelectedPlayerId(null);
  };

  // Use pre-calculated team totals from backend BoxScoreService
  const backendHitting = team_totals?.hitting || {};
  const backendPitching = team_totals?.pitching || {};

  // Totals keyed by config `totalsField` (display-ready), mirroring the full
  // box-score page so the shared column list renders identically.
  const hittingTotals = {
    ab: backendHitting.at_bats || 0,
    r: backendHitting.runs || 0,
    h: backendHitting.hits || 0,
    hr: backendHitting.home_runs || 0,
    rbi: backendHitting.rbi || 0,
    sb: backendHitting.stolen_bases || 0,
    bb: backendHitting.walks || 0,
    so: backendHitting.strikeouts || 0,
    avg: formatBattingAvg(backendHitting.avg) || '.000',
    ops: formatBattingAvg(backendHitting.ops) || '.000',
    obp: formatBattingAvg(backendHitting.obp) || '.000',
    slg: formatBattingAvg(backendHitting.slg) || '.000',
  };

  const pitchingTotals = {
    w: backendPitching.wins || 0,
    l: backendPitching.losses || 0,
    sv: backendPitching.saves || 0,
    ip: (parseFloat(backendPitching.innings_pitched) || 0).toFixed(1),
    h: backendPitching.hits_allowed || 0,
    er: backendPitching.earned_runs || 0,
    bb: backendPitching.walks_allowed || 0,
    so: backendPitching.strikeouts_pitched || 0,
    qs: backendPitching.quality_starts || 0,
    era: formatERA(backendPitching.era) || '0.00',
    whip: formatBattingAvg(backendPitching.whip) || '.000',
    hd: backendPitching.holds || 0,
    hld: backendPitching.holds || 0,
    bs: backendPitching.blown_saves || 0,
  };

  // Bible § 14: columns from league scoring_categories + fixed context prepend,
  // adapted to the MobileDynastyTable column shape (shared single source).
  const rows = activeTab === 'hitting' ? hitting : pitching;
  const columns = buildMobileBoxScoreColumns({
    scoringCategories,
    activeTab,
    onPlayerClick: handlePlayerClick,
  });
  const currentTotals = activeTab === 'hitting' ? hittingTotals : pitchingTotals;

  return (
    <div className="flex-shrink-0 w-80">
      <div style={TILE_CHROME}>
        <div className="p-4">
          {/* Header: Team Logo + Name - DRAGGABLE FOR TILE SCROLLING */}
          <div 
            className="flex items-center gap-3 mb-3 cursor-grab active:cursor-grabbing"
            onMouseDown={(e) => {
              // Enable drag on header for tile scrolling
              e.preventDefault();
              const tile = e.currentTarget.closest('.flex-shrink-0');
              const scrollContainer = tile?.parentElement?.parentElement;
              
              if (scrollContainer && scrollContainer.classList.contains('overflow-x-auto')) {
                const startX = e.pageX;
                const scrollLeft = scrollContainer.scrollLeft;
                
                const handleMouseMove = (moveEvent) => {
                  const walk = (startX - moveEvent.pageX) * 2;
                  scrollContainer.scrollLeft = scrollLeft + walk;
                };
                
                const handleMouseUp = () => {
                  document.removeEventListener('mousemove', handleMouseMove);
                  document.removeEventListener('mouseup', handleMouseUp);
                };
                
                document.addEventListener('mousemove', handleMouseMove);
                document.addEventListener('mouseup', handleMouseUp);
              }
            }}
          >
            {teamLogoUrl ? (
              <img 
                src={teamLogoUrl} 
                alt={teamName}
                className="w-12 h-12 rounded object-cover border-2 border-neutral-700"
                onError={(e) => {
                  e.target.style.display = 'none';
                  if (e.target.nextElementSibling) {
                    e.target.nextElementSibling.style.display = 'flex';
                  }
                }}
              />
            ) : null}
            {!teamLogoUrl && (
              <div className="w-12 h-12 rounded bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center text-black font-bold text-sm">
                {teamName.substring(0, 2).toUpperCase()}
              </div>
            )}
            <div className="flex-1">
              <h4 className={`font-semibold ${dynastyTheme.classes.text.white}`}>{teamName}</h4>
              <p className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>{date}</p>
            </div>
          </div>

          {/* Tabs: Hitters | Pitchers - Using sleek tab styling */}
          <div className={`flex border-b ${dynastyTheme.classes.border.neutral} mb-3`}>
            <button
              onClick={() => setActiveTab('hitting')}
              className={`flex-1 py-2 text-sm font-medium transition-colors ${
                activeTab === 'hitting'
                  ? `${dynastyTheme.classes.text.primary} border-b-2 border-yellow-400`
                  : `${dynastyTheme.classes.text.neutralLight} hover:${dynastyTheme.classes.text.white}`
              }`}
            >
              Hitters ({hitting.length})
            </button>
            <button
              onClick={() => setActiveTab('pitching')}
              className={`flex-1 py-2 text-sm font-medium transition-colors ${
                activeTab === 'pitching'
                  ? `${dynastyTheme.classes.text.primary} border-b-2 border-yellow-400`
                  : `${dynastyTheme.classes.text.neutralLight} hover:${dynastyTheme.classes.text.white}`
              }`}
            >
              Pitchers ({pitching.length})
            </button>
          </div>

          {/* Table Content — shared MobileDynastyTable primitive */}
          <div style={{ marginTop: '8px', border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`, borderRadius: '6px', overflow: 'hidden' }}>
            <MobileDynastyTable
              data={rows}
              columns={columns}
              keyField="player_id"
              tableType="player-list"
              showTotals={rows.length > 0}
              totalsRow={currentTotals}
              maxHeight="400px"
            />
          </div>

          {/* Footer: View Full Link */}
          <div className={`mt-3 pt-3 border-t ${dynastyTheme.classes.border.neutral}`}>
            <button 
              onClick={handleViewFull}
              className={`text-xs ${dynastyTheme.classes.text.primary} hover:underline flex items-center gap-1 transition-colors`}
            >
              View Full Box Score
              <ArrowRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>

      {/* Player Modal */}
      {leagueId && (
        <MobilePlayerModal
          playerId={selectedPlayerId}
          leagueId={leagueId}
          isOpen={isPlayerModalOpen}
          onClose={handleClosePlayer}
        />
      )}
    </div>
  );
};

export default MobileBoxScoreTile;

